import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLocalization } from "@/context/localization-context";
import { cn } from "@/lib/utils";

interface ListPaginationProps {
  /** 0-based */
  page: number;
  totalPages: number | undefined;
  onPageChange: (page: number) => void;
  perPage: number;
  onPerPageChange: (perPage: number) => void;
  total?: number;
  /** "pizzas" / "orders" (shown in the plural form for the total), any other text as it is */
  unit: string;
  pageSizes?: number[];
  /** "Auto" page size (as many rows as the window shows); undefined hides the option */
  autoPageSize?: boolean;
  onAutoPageSizeChange?: (auto: boolean) => void;
  className?: string;
}

/** Footer of the server-paged lists: count, page switcher and page size (thumb-sized on phones). */
export function ListPagination({
  page,
  totalPages,
  onPageChange,
  perPage,
  onPerPageChange,
  total,
  unit,
  pageSizes = [10, 20, 30, 40, 50],
  autoPageSize,
  onAutoPageSizeChange,
  className,
}: ListPaginationProps) {
  const { t, tp, formatNumber } = useLocalization();
  const pages = Math.max(totalPages ?? 1, 1);
  const canPrev = page > 0;
  const canNext = page + 1 < pages;
  const unitText = (count: number) =>
    unit === "pizzas"
      ? tp("list.pagination.unitPizzas", count)
      : unit === "orders"
        ? tp("list.pagination.unitOrders", count)
        : unit;

  return (
    <div className={cn("flex flex-wrap items-center justify-between gap-x-4 gap-y-2", className)}>
      <div className="text-sm text-muted-foreground">
        {total !== undefined ? (
          <>
            <span className="font-medium tabular-nums text-foreground">{formatNumber(total)}</span>{" "}
            {unitText(total)}
          </>
        ) : (
          <span>&nbsp;</span>
        )}
      </div>
      <div className="flex items-center gap-1.5">
        <Button
          variant="outline"
          size="icon"
          className="hidden h-8 w-8 sm:inline-flex"
          onClick={() => onPageChange(0)}
          disabled={!canPrev}
          aria-label={t("common.firstPage")}
        >
          <ChevronsLeftIcon />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 sm:h-8 sm:w-8"
          onClick={() => onPageChange(page - 1)}
          disabled={!canPrev}
          aria-label={t("common.previousPage")}
        >
          <ChevronLeftIcon />
        </Button>
        <div className="min-w-20 text-center text-sm tabular-nums">
          {page + 1} <span className="text-muted-foreground">/ {pages}</span>
        </div>
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 sm:h-8 sm:w-8"
          onClick={() => onPageChange(page + 1)}
          disabled={!canNext}
          aria-label={t("common.nextPage")}
        >
          <ChevronRightIcon />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="hidden h-8 w-8 sm:inline-flex"
          onClick={() => onPageChange(pages - 1)}
          disabled={!canNext}
          aria-label={t("common.lastPage")}
        >
          <ChevronsRightIcon />
        </Button>
      </div>
      <div className="flex items-center gap-2">
        <span className="hidden text-sm text-muted-foreground sm:inline">{t("common.perPage")}</span>
        <Select
          value={autoPageSize ? "auto" : `${perPage}`}
          onValueChange={(value) => {
            if (value === "auto") {
              onAutoPageSizeChange?.(true);
              return;
            }
            onAutoPageSizeChange?.(false);
            onPerPageChange(Number(value));
          }}
        >
          <SelectTrigger className={cn("h-8", autoPageSize ? "w-[6.5rem]" : "w-[4.5rem]")} aria-label={t("list.pagination.rowsPerPage")}>
            <SelectValue placeholder={perPage}>
              {autoPageSize ? t("common.autoPageSize", { count: perPage }) : perPage}
            </SelectValue>
          </SelectTrigger>
          <SelectContent side="top">
            {onAutoPageSizeChange && (
              <SelectItem value="auto">{t("common.autoPageSizeOption")}</SelectItem>
            )}
            {pageSizes.map((size) => (
              <SelectItem key={size} value={`${size}`}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
